import { ChevronLeft, ChevronRight } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router'
import { Avatar } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { Table, TBody, TD, TH, THead, TR } from '@/components/ui/table'
import { fmtDate } from '@/features/shared/format'
import { paginate } from '../lib/listStats'
import type { Personel } from '../types'
import { PartnerTags } from './PartnerTags'
import { PersonelRowActions } from './PersonelRowActions'
import { PersonelStatusPill, SoftTag } from './StatusPill'
import { calismaTuruLabelKey } from './statusTone'

const PAGE_SIZE = 10

export function PersonelTable({ rows, page, onPage }: { rows: Personel[]; page: number; onPage: (n: number) => void }) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const pg = paginate(rows, page, PAGE_SIZE)

  if (rows.length === 0) {
    return <div className="px-5 py-16 text-center text-sm text-muted-foreground">{t('hr.personel.empty')}</div>
  }

  return (
    <>
      <ul className="divide-y divide-border/70 md:hidden">
        {pg.items.map((p) => (
          <li key={p.id} className="flex items-start transition-colors hover:bg-muted/40">
            <Link to={`/hr/personel/${p.id}`} className="flex min-w-0 flex-1 items-start gap-3 py-3.5 pl-4 pr-1">
              <Avatar name={p.ad} className="size-10" />
              <div className="min-w-0 flex-1 space-y-1">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="flex min-w-0 items-center gap-1.5">
                      <span className="truncate text-sm font-semibold">{p.ad}</span>
                      <PartnerTags personel={p} />
                    </div>
                    <div className="truncate text-xs text-muted-foreground">
                      {p.pozisyon} · {p.departman}
                    </div>
                  </div>
                  <span className="shrink-0 whitespace-nowrap">
                    <PersonelStatusPill durum={p.durum} />
                  </span>
                </div>
                <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                  <span className="tabular-nums">{p.sicil}</span>
                  <span aria-hidden>·</span>
                  <span>{fmtDate(p.ise_giris)}</span>
                  <SoftTag>{t(calismaTuruLabelKey(p.calisma_turu))}</SoftTag>
                </div>
              </div>
            </Link>
            <div className="shrink-0 pr-2 pt-3">
              <PersonelRowActions personel={p} />
            </div>
          </li>
        ))}
      </ul>
      <Table className="hr-table hidden md:table">
        <THead>
          <TR className="hover:bg-transparent">
            <TH>{t('hr.personel.columns.personel')}</TH>
            <TH>{t('hr.personel.columns.departman')}</TH>
            <TH>{t('hr.personel.columns.pozisyon')}</TH>
            <TH>{t('hr.personel.columns.giris')}</TH>
            <TH>{t('hr.personel.columns.tur')}</TH>
            <TH>{t('hr.personel.columns.durum')}</TH>
            <TH className="w-12">{t('hr.personel.columns.islem')}</TH>
          </TR>
        </THead>
        <TBody>
          {pg.items.map((p) => (
            <TR key={p.id} className="cursor-pointer" onClick={() => navigate(`/hr/personel/${p.id}`)}>
              <TD>
                <div className="flex items-center gap-3">
                  <Avatar name={p.ad} className="size-9" />
                  <div className="min-w-0">
                    <div className="flex min-w-0 items-center gap-1.5">
                      <span className="truncate font-semibold">{p.ad}</span>
                      <PartnerTags personel={p} />
                    </div>
                    <div className="truncate text-xs tabular-nums text-muted-foreground">{p.sicil}</div>
                  </div>
                </div>
              </TD>
              <TD className="text-muted-foreground">{p.departman}</TD>
              <TD className="font-medium">{p.pozisyon}</TD>
              <TD className="whitespace-nowrap text-muted-foreground">{fmtDate(p.ise_giris)}</TD>
              <TD>
                <SoftTag>{t(calismaTuruLabelKey(p.calisma_turu))}</SoftTag>
              </TD>
              <TD>
                <PersonelStatusPill durum={p.durum} />
              </TD>
              <TD>
                <PersonelRowActions personel={p} />
              </TD>
            </TR>
          ))}
        </TBody>
      </Table>
      <div className="flex flex-wrap items-center justify-between gap-3 border-t bg-muted/20 px-5 py-3.5 text-xs text-muted-foreground">
        <span>{t('hr.personel.pagination', { total: rows.length, from: pg.from, to: pg.to })}</span>
        <div className="flex items-center gap-1">
          <Button
            variant="outline"
            size="icon"
            className="size-8 bg-card"
            disabled={pg.current === 1}
            aria-label={t('hr.personel.sayfaOnceki')}
            onClick={() => onPage(pg.current - 1)}
          >
            <ChevronLeft className="size-4" />
          </Button>
          {Array.from({ length: pg.pages }, (_, i) => i + 1).map((n) => (
            <Button
              key={n}
              size="sm"
              variant={n === pg.current ? 'default' : 'outline'}
              className="h-8 min-w-8 px-2.5"
              aria-label={t('hr.personel.sayfa', { n })}
              aria-current={n === pg.current ? 'page' : undefined}
              onClick={() => onPage(n)}
            >
              {n}
            </Button>
          ))}
          <Button
            variant="outline"
            size="icon"
            className="size-8 bg-card"
            disabled={pg.current === pg.pages}
            aria-label={t('hr.personel.sayfaSonraki')}
            onClick={() => onPage(pg.current + 1)}
          >
            <ChevronRight className="size-4" />
          </Button>
        </div>
      </div>
    </>
  )
}
